import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAt, faEnvelope, faLocationDot, faPhone } from "@fortawesome/free-solid-svg-icons";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";

import SEO, { pageSEO } from "../components/seo.component";
import constants from "../constants/constants";
import { useUserParams } from "../hooks/useUserParams";

export default function About() {
  const seoConfig = pageSEO.about();
  const {
    getCurrentAgentName,
    getCurrentAgentTitle,
    getCurrentAgentNumber,
    getCurrentAgentEmail,
    getCurrentBrokerage,
    getCurrentAbout,
    getCurrentHeadshot,
  } = useUserParams();

  return (
    <>
      <SEO {...seoConfig} />

      <section className="py-32 bg-[#fffdfa] overflow-hidden">
        <div className="mx-auto max-w-[1200px] px-6 lg:px-16">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-20"
          >
            <div className="flex items-center gap-5 mb-8">
              <div className="h-px w-16 bg-[#8b8172]" />

              <span className="text-xs uppercase tracking-[0.45em] text-[#6d655b]">
                About Me
              </span>
            </div>

            <h1 className="font-serif text-5xl lg:text-6xl leading-[1.05] text-[#2b2925]">
              Helping Families
              <br />
              Find A Place
              <br />
              To Call Home.
            </h1>
          </motion.div>

          {/* Main Content */}
          <div className="grid lg:grid-cols-12 gap-16 items-start">
            {/* Image */}
            <motion.div
              initial={{ opacity: 0, x: -60 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="lg:col-span-5"
            >
              <div className="relative">
                {/* Frame */}
                <div className="absolute -top-9 -left-9 h-full w-full border-[15px] border-[#d3d2d0]/70" />

                <img
                  src={getCurrentHeadshot()}
                  alt={getCurrentAgentName()}
                  className="relative h-[700px] w-full object-cover"
                />
                {/* Experience Label */}
                <div className="absolute bottom-0 left-0 bg-[#d3d2d0] px-8 py-6">
                  <p className="font-serif text-5xl text-[#2b2925]">
                    15+
                  </p>
                  <p className="mt-2 text-xs uppercase tracking-[0.35em] text-[#6d655b]">
                    Years Of Excellence
                  </p>
                </div>
              </div>
            </motion.div>


            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: 60 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="lg:col-span-7"
            >
              <h2 className="font-serif text-4xl lg:text-5xl leading-tight text-[#2b2925]">
                {getCurrentAgentName()}
              </h2>

              <p className="mt-4 text-sm uppercase tracking-[0.35em] text-[#6d655b]">
                {getCurrentAgentTitle()}
              </p>

              <p className="mt-2 text-[#6d655b]">
                {getCurrentBrokerage()}
              </p>

              <p className="mt-6 max-w-3xl leading-9 text-[#5f594f] whitespace-pre-line">
                {getCurrentAbout()}
              </p>

              <Link
                to="/contact"
                className="inline-flex mt-7 border border-[#8b8172] px-10 py-4 text-xs uppercase tracking-[0.35em] text-[#2b2925] transition-all duration-300 hover:bg-[#2b2925] hover:text-[#eee9df]"
              >
                Get In Touch
              </Link>
            </motion.div>
          </div>

          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mt-32 grid lg:grid-cols-3 gap-12"
          >
            <div className="border-[15px] border-[#d3d2d0]/70 p-5">
              <div className="flex items-start gap-5">
                <FontAwesomeIcon
                  icon={faPhone}
                  className="mt-1 text-xl text-[#8b8172]"
                />

                <div>
                  <h4 className="text-xs uppercase tracking-[0.35em] text-[#6d655b]">
                    Phone
                  </h4>

                  <p className="mt-4 text-lg text-[#2b2925]">
                    {getCurrentAgentNumber()}
                  </p>
                </div>
              </div>
            </div>

            <div className="border-[15px] border-[#d3d2d0]/70 p-5">
              <div className="flex items-start gap-5">
                <FontAwesomeIcon
                  icon={faEnvelope}
                  className="mt-1 text-xl text-[#8b8172]"
                />

                <div>
                  <h4 className="text-xs uppercase tracking-[0.35em] text-[#6d655b]">
                    Email
                  </h4>

                  <p className="mt-4 text-lg text-[#2b2925]">
                    {getCurrentAgentEmail()}
                  </p>
                </div>
              </div>
            </div>

            <div className="border-[15px] border-[#d3d2d0]/70 p-5">
              <div className="flex items-start gap-5">
                <FontAwesomeIcon
                  icon={faLocationDot}
                  className="mt-1 text-xl text-[#8b8172]"
                />

                <div>
                  <h4 className="text-xs uppercase tracking-[0.35em] text-[#6d655b]">
                    Office
                  </h4>

                  <p className="mt-4 leading-8 text-[#5f594f]">
                    {constants.AGENT_ADDRESS}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
}